import { ShipShape, ShapeType } from '@components/Renderable';

export interface Point {
  x: number;
  y: number;
}

export interface ShipGeometry {
  /** Closed hull outline, unit-scaled, nose pointing along +x */
  hull: Point[];
  /** Decorative panel lines drawn over the hull */
  panels: Point[][];
  /** Engine nozzle positions; flames are drawn backwards from these */
  engines: Point[];
  /** Canopy / sensor ellipse */
  cockpit?: { x: number; y: number; rx: number; ry: number; color: string };
  /** Blinking running lights */
  lights: Point[];
  /** Running light radius in hull units (default 0.06) */
  lightRadius?: number;
  /** Lights blink one after another instead of together */
  lightChase?: boolean;
  /** Recessed hangar bays: centre, width and height in hull units */
  hangars?: { x: number; y: number; w: number; h: number }[];
  engineColor: string;
}

export const SHIP_SHAPES: ShipShape[] = [
  'fighter', 'dart', 'gunship', 'longbow', 'warden', 'interceptor', 'lancer', 'bomber', 'swarmer',
  'dreadnought', 'aegis', 'minelayer', 'carrier', 'torpedo', 'mothership'
];

export function isShipShape(shape: ShapeType): shape is ShipShape {
  return (SHIP_SHAPES as ShapeType[]).includes(shape);
}

/**
 * Builds a symmetric hull from its upper half. `half` runs from the nose to the
 * tail with y <= 0; points on the axis are not duplicated.
 */
function mirrorHull(half: Point[]): Point[] {
  const lower = half
    .filter(p => p.y !== 0)
    .reverse()
    .map(p => ({ x: p.x, y: -p.y }));
  return [...half, ...lower];
}

/** Returns a panel line together with its mirror image. */
function mirrored(line: Point[]): Point[][] {
  return [line, line.map(p => ({ x: p.x, y: -p.y }))];
}

const p = (x: number, y: number): Point => ({ x, y });

const GEOMETRY: Record<ShipShape, ShipGeometry> = {
  fighter: {
    hull: mirrorHull([p(1.3, 0), p(0.55, -0.18), p(0.15, -0.26), p(-0.3, -0.92), p(-0.72, -0.96), p(-0.52, -0.36), p(-0.82, -0.3), p(-0.74, 0)]),
    panels: [...mirrored([p(0.4, -0.2), p(-0.5, -0.34)]), ...mirrored([p(-0.28, -0.6), p(-0.62, -0.62)])],
    engines: [p(-0.8, -0.17), p(-0.8, 0.17)],
    cockpit: { x: 0.45, y: 0, rx: 0.32, ry: 0.11, color: '#bff4ff' },
    lights: [p(-0.7, -0.93), p(-0.7, 0.93)],
    engineColor: '#55ccff',
  },
  dart: {
    hull: mirrorHull([p(1.4, 0), p(0.3, -0.16), p(-0.1, -0.2), p(-0.55, -0.75), p(-0.8, -0.72), p(-0.6, -0.18), p(-0.7, 0)]),
    panels: mirrored([p(0.1, -0.18), p(-0.6, -0.6)]),
    engines: [p(-0.7, 0)],
    cockpit: { x: 0.55, y: 0, rx: 0.3, ry: 0.09, color: '#c8fbff' },
    lights: [p(-0.75, -0.7), p(-0.75, 0.7)],
    engineColor: '#66e6ff',
  },
  gunship: {
    hull: mirrorHull([p(1.1, 0), p(0.85, -0.3), p(0.35, -0.4), p(0.25, -0.85), p(-0.35, -0.9), p(-0.5, -0.5), p(-0.95, -0.45), p(-1.0, 0)]),
    panels: [
      ...mirrored([p(0.35, -0.4), p(-0.5, -0.5)]),
      ...mirrored([p(0.2, -0.7), p(-0.3, -0.7)]),
      [p(0.9, 0), p(-0.8, 0)],
    ],
    engines: [p(-1.0, -0.28), p(-1.0, 0), p(-1.0, 0.28)],
    cockpit: { x: 0.5, y: 0, rx: 0.22, ry: 0.15, color: '#bfd0ff' },
    lights: [p(0.2, -0.85), p(0.2, 0.85)],
    engineColor: '#6f86ff',
  },
  longbow: {
    hull: mirrorHull([p(1.55, 0), p(0.5, -0.1), p(0.0, -0.3), p(-0.45, -0.65), p(-0.9, -0.62), p(-0.7, -0.22), p(-0.9, 0)]),
    panels: [[p(1.45, 0), p(-0.3, 0)], ...mirrored([p(0.0, -0.3), p(-0.7, -0.55)])],
    engines: [p(-0.88, -0.14), p(-0.88, 0.14)],
    cockpit: { x: 0.2, y: 0, rx: 0.22, ry: 0.1, color: '#dcd4ff' },
    lights: [p(1.5, 0)],
    engineColor: '#a699ff',
  },
  warden: {
    hull: mirrorHull([p(1.1, 0), p(0.6, -0.45), p(0.0, -0.6), p(-0.35, -0.95), p(-0.75, -0.8), p(-0.6, -0.35), p(-0.85, 0)]),
    panels: [...mirrored([p(0.0, -0.6), p(-0.6, -0.35)]), ...mirrored([p(0.6, -0.45), p(0.0, -0.1)])],
    engines: [p(-0.82, -0.2), p(-0.82, 0.2)],
    cockpit: { x: 0.35, y: 0, rx: 0.3, ry: 0.3, color: '#bafff6' },
    lights: [p(-0.35, -0.92), p(-0.35, 0.92)],
    engineColor: '#4ff0e0',
  },
  interceptor: {
    hull: mirrorHull([p(1.2, 0), p(0.25, -0.28), p(0.65, -0.82), p(-0.2, -0.72), p(-0.72, -0.3), p(-0.58, 0)]),
    panels: mirrored([p(0.2, -0.26), p(0.45, -0.7)]),
    engines: [p(-0.62, 0)],
    cockpit: { x: 0.35, y: 0, rx: 0.22, ry: 0.1, color: '#ffd0c0' },
    lights: [p(0.6, -0.78), p(0.6, 0.78)],
    engineColor: '#ff9955',
  },
  lancer: {
    hull: mirrorHull([p(1.5, 0), p(0.2, -0.16), p(-0.2, -0.56), p(-0.92, -0.62), p(-0.62, -0.2), p(-0.9, 0)]),
    panels: [[p(1.3, 0), p(-0.5, 0)], ...mirrored([p(-0.15, -0.5), p(-0.8, -0.56)])],
    engines: [p(-0.86, -0.3), p(-0.86, 0.3)],
    cockpit: { x: 0.1, y: 0, rx: 0.18, ry: 0.09, color: '#f3b8ff' },
    lights: [p(1.35, 0)],
    engineColor: '#dd66ff',
  },
  bomber: {
    hull: mirrorHull([p(1.0, 0), p(0.62, -0.34), p(0.2, -0.4), p(0.1, -0.9), p(-0.6, -0.95), p(-0.7, -0.45), p(-0.92, -0.3), p(-0.92, 0)]),
    panels: [...mirrored([p(0.1, -0.62), p(-0.6, -0.62)]), ...mirrored([p(0.2, -0.4), p(-0.7, -0.45)])],
    engines: [p(-0.92, -0.15), p(-0.92, 0.15)],
    cockpit: { x: 0.5, y: 0, rx: 0.2, ry: 0.14, color: '#ffe0b0' },
    lights: [p(0.0, -0.9), p(0.0, 0.9)],
    engineColor: '#ffaa33',
  },
  swarmer: {
    hull: mirrorHull([p(1.2, 0), p(-0.6, -0.8), p(-0.3, 0)]),
    panels: [[p(0.9, 0), p(-0.2, 0)]],
    engines: [p(-0.35, 0)],
    lights: [],
    engineColor: '#ddff44',
  },
  dreadnought: {
    hull: mirrorHull([p(1.3, 0), p(1.0, -0.25), p(0.4, -0.35), p(0.3, -0.62), p(-0.42, -0.66), p(-0.52, -0.45), p(-1.0, -0.5), p(-1.1, -0.2), p(-1.0, 0)]),
    panels: [
      [p(1.0, 0), p(-0.9, 0)],
      ...mirrored([p(0.3, -0.35), p(-0.5, -0.35)]),
      ...mirrored([p(0.25, -0.5), p(-0.35, -0.5)]),
      ...mirrored([p(-0.52, -0.45), p(-0.52, -0.2)]),
    ],
    engines: [p(-1.05, -0.35), p(-1.08, 0), p(-1.05, 0.35)],
    cockpit: { x: 0.55, y: 0, rx: 0.2, ry: 0.13, color: '#ffb0c0' },
    lights: [p(0.3, -0.6), p(0.3, 0.6), p(-0.9, -0.48), p(-0.9, 0.48)],
    engineColor: '#ff5566',
  },
  aegis: {
    hull: mirrorHull([p(1.1, 0), p(0.2, -0.5), p(-0.2, -0.95), p(-0.5, -0.5), p(-0.8, 0)]),
    panels: [...mirrored([p(0.2, -0.5), p(-0.5, -0.5)]), [p(0.9, 0), p(-0.6, 0)]],
    engines: [p(-0.76, 0)],
    cockpit: { x: 0.2, y: 0, rx: 0.26, ry: 0.26, color: '#b0fff0' },
    lights: [p(-0.2, -0.9), p(-0.2, 0.9)],
    engineColor: '#33ffcc',
  },
  minelayer: {
    hull: mirrorHull([p(0.9, 0), p(0.7, -0.45), p(0.1, -0.7), p(-0.6, -0.66), p(-0.9, -0.4), p(-0.6, -0.15), p(-0.72, 0)]),
    panels: [...mirrored([p(0.1, -0.7), p(0.0, -0.2)]), ...mirrored([p(-0.6, -0.15), p(-0.2, -0.15)])],
    engines: [p(-0.88, -0.42), p(-0.88, 0.42)],
    cockpit: { x: 0.45, y: 0, rx: 0.2, ry: 0.2, color: '#fff2a0' },
    lights: [p(-0.66, 0)],
    engineColor: '#ffdd33',
  },
  carrier: {
    hull: mirrorHull([p(1.3, 0), p(1.1, -0.3), p(0.6, -0.4), p(0.5, -0.7), p(-0.6, -0.7), p(-0.7, -0.45), p(-1.2, -0.45), p(-1.2, 0)]),
    panels: [
      ...mirrored([p(0.45, -0.55), p(-0.55, -0.55)]),
      ...mirrored([p(0.6, -0.4), p(-1.1, -0.4)]),
      [p(1.1, 0), p(-1.1, 0)],
    ],
    engines: [p(-1.2, -0.32), p(-1.2, -0.11), p(-1.2, 0.11), p(-1.2, 0.32)],
    cockpit: { x: 0.8, y: 0, rx: 0.18, ry: 0.12, color: '#e0ffb0' },
    lights: [p(0.4, -0.66), p(0.0, -0.66), p(-0.4, -0.66), p(0.4, 0.66), p(0.0, 0.66), p(-0.4, 0.66)],
    engineColor: '#ccff44',
  },
  torpedo: {
    hull: mirrorHull([p(1.2, 0), p(0.6, -0.25), p(-0.6, -0.25), p(-0.9, -0.55), p(-0.9, 0)]),
    panels: [],
    engines: [p(-0.9, 0)],
    lights: [p(1.0, 0)],
    engineColor: '#ffcc66',
  },
  // The player's home base: command prow, flight deck with hangar bays along the spine, heavy drive block
  mothership: {
    hull: mirrorHull([
      p(1.45, 0), p(1.3, -0.1), p(1.05, -0.17), p(0.95, -0.3), p(0.72, -0.33), p(0.62, -0.46),
      p(0.3, -0.5), p(-0.15, -0.5), p(-0.25, -0.44), p(-0.62, -0.44), p(-0.72, -0.5),
      p(-1.02, -0.5), p(-1.12, -0.4), p(-1.3, -0.4), p(-1.36, -0.22), p(-1.3, 0),
    ]),
    panels: [
      [p(1.3, 0), p(0.78, 0)],
      [p(-0.78, 0), p(-1.25, 0)],
      ...mirrored([p(0.95, -0.3), p(0.72, -0.3)]),
      ...mirrored([p(0.62, -0.42), p(-0.72, -0.42)]),
      ...mirrored([p(0.62, -0.46), p(0.62, -0.26)]),
      ...mirrored([p(-0.25, -0.44), p(-0.25, -0.3)]),
      ...mirrored([p(-0.72, -0.5), p(-0.72, -0.26)]),
      ...mirrored([p(-1.02, -0.5), p(-1.02, -0.2)]),
      ...mirrored([p(1.05, -0.17), p(0.8, -0.12)]),
      ...mirrored([p(-1.12, -0.4), p(-1.3, -0.3)]),
    ],
    engines: [p(-1.33, -0.3), p(-1.35, -0.1), p(-1.35, 0.1), p(-1.33, 0.3)],
    cockpit: { x: 1.02, y: 0, rx: 0.13, ry: 0.07, color: '#c9e6ff' },
    lights: [
      p(1.4, 0), p(0.62, -0.46), p(0.62, 0.46), p(0.3, -0.5), p(0.3, 0.5), p(-0.15, -0.5), p(-0.15, 0.5),
      p(-0.72, -0.5), p(-0.72, 0.5), p(-1.02, -0.5), p(-1.02, 0.5), p(-1.3, -0.4), p(-1.3, 0.4),
    ],
    lightRadius: 0.014,
    lightChase: true,
    hangars: [
      { x: -0.02, y: 0, w: 1.12, h: 0.36 },
      { x: 0.2, y: -0.37, w: 0.62, h: 0.08 },
      { x: 0.2, y: 0.37, w: 0.62, h: 0.08 },
    ],
    engineColor: '#7fb8ff',
  },
};

export function getShipGeometry(shape: ShipShape): ShipGeometry {
  return GEOMETRY[shape];
}
